import type { ComboboxOption } from "@sentricmusic/ui";

import { useCallback, useRef, useState } from "react";

import { apiCall } from "@/api/shared";

import type { VenueSearchResponse, VenueSearchResult } from "../types/gigs.types";

export default function useVenueSearch() {
  const [venueOptions, setVenueOptions] = useState<ComboboxOption[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);

  // Kept as a ref so changes to selected country don't recreate handleVenueSearch
  const selectedCountryRef = useRef<string>("");

  const handleVenueSearch = useCallback(async (query: string): Promise<void> => {
    if (!query) {
      setVenueOptions([]);
      return;
    }
    setVenueLoading(true);
    try {
      const results = await apiCall({
        endpoint: `/venue-search?name=${encodeURIComponent(query)}&countryiso=${selectedCountryRef.current}`,
        method: "get",
      }) as VenueSearchResponse;
      setVenueOptions(
        results.results.map((item) => {
          const normalised: VenueSearchResult = {
            id: String(item.id),
            venueNameNew: item.venueNameNew,
            venueCity: item.venueCity,
            addressLine1: item.addressLine1,
            fullPostCode: item.fullPostCode,
            premisesCategoryId: item.PremisesCategoryId ?? item.premisesCategoryId,
            premisesTypeId: item.PremisesTypeId ?? item.premisesTypeId,
            isApproved: Boolean(item.IsApproved ?? item.isApproved),
            prsVenueID: item.PrsVenueID ?? item.prsVenueID ?? "",
          };
          return {
            label: `${normalised.venueNameNew}, ${normalised.venueCity}`,
            value: JSON.stringify(normalised),
          };
        }),
      );
    }
    catch {
      setVenueOptions([]);
    }
    finally {
      setVenueLoading(false);
    }
  }, []);

  return {
    venueOptions,
    setVenueOptions,
    venueLoading,
    selectedCountryRef,
    handleVenueSearch,
  };
}
